import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getServicesManager } from '../../../services';
import { InterviewPortalInfo, InterviewBotCall, InterviewBot } from '../../../interfaces/interview';

export type InterviewStep = 'loading' | 'login' | 'bot-selection' | 'history' | 'error';

export interface InterviewState {
  // Portal info
  portalId: string | null;
  portalInfo: InterviewPortalInfo | null;
  
  // Authentication
  userEmail: string;
  password: string;
  isAuthenticated: boolean;
  
  // UI state
  currentStep: InterviewStep;
  loading: boolean;
  error: string | null;
  
  // Data
  bots: InterviewBot[];
  userCalls: InterviewBotCall[];
  
  // Call state
  creatingCall: boolean;
}

const initialState: InterviewState = {
  portalId: null,
  portalInfo: null,
  userEmail: '',
  password: '',
  isAuthenticated: false,
  currentStep: 'loading',
  loading: false,
  error: null,
  bots: [],
  userCalls: [],
  creatingCall: false,
};

// Async thunks
export const fetchPortalInfo = createAsyncThunk(
  'interview/fetchPortalInfo',
  async (portalId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager();
      const portalData = await servicesManager.getInterviewPortalInfo(portalId);
      
      if (!portalData) {
        throw new Error('Portal not found');
      }
      
      return portalData;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch portal info');
    }
  }
);

export const fetchPortalBots = createAsyncThunk(
  'interview/fetchPortalBots',
  async (portalId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager();
      const botsData = await servicesManager.getInterviewPortalBots(portalId);
      
      if (!botsData) {
        throw new Error('Failed to fetch bots');
      }
      
      return botsData;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch bots');
    }
  }
);

export const authenticateUser = createAsyncThunk(
  'interview/authenticateUser',
  async ({ portalId, email, password }: { portalId: string; email: string; password: string }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager();
      const portalData = await servicesManager.getInterviewPortalInfo(portalId);
      
      if (!portalData) {
        throw new Error('Portal not found');
      }

      // Validate password against portal password
      if (portalData.password !== password) {
        return rejectWithValue('Invalid password');
      }

      // Try to fetch user's existing calls using the validated credentials
      const callsData = await servicesManager.getInterviewBotCalls(portalId, email, password);
      
      return { 
        email, 
        password, 
        calls: callsData || [] 
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Authentication failed');
    }
  }
);

export const createBotCall = createAsyncThunk(
  'interview/createBotCall',
  async ({ portalId, userEmail, password, botId }: { 
    portalId: string; 
    userEmail: string; 
    password: string; 
    botId: string;
  }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager();
      const callData = await servicesManager.createInterviewBotCall({
        portal_id: portalId,
        user_email: userEmail,
        password: password,
        bot_id: botId,
      });
      
      if (!callData) {
        throw new Error('Failed to start bot call');
      }
      
      return callData;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to start bot call');
    }
  }
);

export const refreshUserCalls = createAsyncThunk(
  'interview/refreshUserCalls',
  async ({ portalId, email, password }: { portalId: string; email: string; password: string }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager();
      const callsData = await servicesManager.getInterviewBotCalls(portalId, email, password);
      
      return callsData || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to refresh calls');
    }
  }
);


// Slice
export const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    setPortalId: (state, action: PayloadAction<string>) => {
      state.portalId = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<InterviewStep>) => {
      state.currentStep = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.userEmail = '';
      state.password = '';
      state.isAuthenticated = false;
      state.userCalls = [];
      state.currentStep = 'login';
      state.error = null;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    addUserCall: (state, action: PayloadAction<InterviewBotCall>) => {
      const incoming = action.payload
      const idx = state.userCalls.findIndex(c => c.bot_call_id === incoming.bot_call_id)
      if (idx === -1) {
        state.userCalls.unshift(incoming)
      } else {
        state.userCalls[idx] = { ...state.userCalls[idx], ...incoming }
      }
    },
    markCallAsCompleted: (state, action: PayloadAction<{
      botCallId: string;
      transcript: string;
      callDurationSeconds?: number;
    }>) => {
      const { botCallId, transcript, callDurationSeconds } = action.payload;
      const callIndex = state.userCalls.findIndex(call => call.bot_call_id === botCallId);
      const update = {
        transcript,
        call_duration_seconds: callDurationSeconds,
        ended_at: new Date().toISOString(),
      } as Partial<InterviewBotCall>
      if (callIndex !== -1) {
        state.userCalls[callIndex] = { ...state.userCalls[callIndex], ...update } as InterviewBotCall
      } else {
        // Upsert if not present so attempts update immediately
        state.userCalls.unshift({
          bot_call_id: botCallId,
          portal_id: state.portalId || '',
          user_email: state.userEmail,
          bot_id: '',
          created_at: new Date().toISOString(),
          ...update,
        } as InterviewBotCall)
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch portal info
    builder
      .addCase(fetchPortalInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortalInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.portalInfo = action.payload;
        state.currentStep = 'login';
      })
      .addCase(fetchPortalInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.currentStep = 'error';
      });

    // Fetch portal bots
    builder
      .addCase(fetchPortalBots.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPortalBots.fulfilled, (state, action) => {
        state.loading = false;
        state.bots = action.payload;
      })
      .addCase(fetchPortalBots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Authenticate user
    builder
      .addCase(authenticateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authenticateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userEmail = action.payload.email;
        state.password = action.payload.password;
        state.userCalls = action.payload.calls;
        state.isAuthenticated = true;
        state.currentStep = 'bot-selection';
      })
      .addCase(authenticateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.currentStep = 'login'; // Stay on login page when auth fails
      });

    // Create bot call
    builder
      .addCase(createBotCall.pending, (state) => {
        state.creatingCall = true;
        state.error = null;
      })
      .addCase(createBotCall.fulfilled, (state, action) => {
        state.creatingCall = false;
        // Note: userCalls will be updated by refreshUserCalls action after bot call creation
      })
      .addCase(createBotCall.rejected, (state, action) => {
        state.creatingCall = false;
        state.error = action.payload as string;
      });

    // Refresh user calls
    builder
      .addCase(refreshUserCalls.fulfilled, (state, action) => {
        state.userCalls = action.payload;
      });

  },
});

export const { setPortalId, setCurrentStep, clearError, logout, resetState, addUserCall, markCallAsCompleted } = interviewSlice.actions;

export default interviewSlice.reducer;